/* eslint-disable max-lines -- Pre-existing violation */
import {captureException} from '@sentry/browser';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {APPLY_QUERY_FETCH_SETTLE_TIMEOUT_MS} from '../constants/APPLY_QUERY_FETCH_SETTLE_TIMEOUT_MS';
import {DEFAULT_BEHAVIOR} from '../constants/defaultBehavior';
import {DEFAULT_SETTINGS} from '../constants/defaultSettings';
import {
  selectGridSettings,
  type EagGridState,
  type EagGridStore,
} from '../store/createEagGridStore';
import {selectRowSelectMode} from '../store/selectors/useBehavior';
import type {ActionsAlignment} from '../types/ActionsAlignment';
import type {CellsV5, Preset, PresetDetail, VirtualPreset} from '../types/Api';
import type {GetDataQueryResponse} from '../types/api/data';
import type {ExternalFilters} from '../types/ExternalFilters';
import type {Filters} from '../types/Filters';
import type {QueryFilterObject} from '../types/QueryFilterObject';
import type {RowData} from '../types/RowData';
import {
  APPLY_EAGRID_QUERY_EVENT,
  type ApplyEagridQueryCustomEvent,
} from '../utils/applyDataGridQuery';
import {clampSettingsToLockedHidden} from '../utils/clampSettingsToLockedHidden';
import {computeSortModelKey} from '../utils/computeSortModelKey';
import {computeVisibleColumnsKey} from '../utils/computeVisibleColumnsKey';
import {dispatchApplyDataGridQueryResult} from '../utils/dispatchApplyDataGridQueryResult';
import {filterTechnicalArray} from '../utils/filterTechnicalArray';
import {getPresetStates} from '../utils/getPresetStates';
import {isCustomPresetDirty} from '../utils/isCustomPresetDirty';
import {isPresetColumnsSettingsInvalid} from '../utils/isPresetColumnsSettingsInvalid';
import {isPresetCustom} from '../utils/isPresetCustom';
import {isVirtualPresetEqual} from '../utils/isVirtualPresetEqual';
import {migratePresetLocalPrefs} from '../utils/migratePresetLocalPrefs';
import {
  reconcilePresetWithQueryModifier,
  type ReconcileResult,
} from '../utils/reconcilePresetWithQueryModifier';
import {REFETCH_EAGRID_EVENT, type RefetchEagridCustomEvent} from '../utils/refreshDatagrid';
import {resolveExternalRowSelection} from '../utils/resolveExternalRowSelection';
import {
  SELECT_EAGRID_ROWS_EVENT,
  type SelectDatagridRowsTarget,
  type SelectEagridRowsCustomEvent,
} from '../utils/selectDatagridRows';
import {stripLocalGridSettings} from '../utils/stripLocalGridSettings';
import {unwrapFilterValuesRaw} from '../utils/unwrapFilterValuesRaw';
import {
  getPageFromUrl,
  getSearchParamsByGridCode,
  setPageInUrl,
} from '../utils/url/getSearchParamsByGridCode';
import {setSearchParamsByGridCode} from '../utils/url/setSearchParamsByGridCode';
import {GridDataEngine, type GridDataEngineInputs} from './GridDataEngine';
import {clearPageDataCacheForGrid} from './pageDataCache';
import {waitForFetchSettle} from './waitForFetchSettle';

/** Result of resolving the active preset + its initial dataQuery during the cold load. */
type Resolution = {
  activePreset: PresetDetail | VirtualPreset | null;
  dataQuery: GetDataQueryResponse | null;
};

const isReconcileResult = (
  v: GetDataQueryResponse | ReconcileResult | null | undefined | void
): v is ReconcileResult => !!v && typeof v === 'object' && 'dataQuery' in v;

/** Per-render volatile inputs pushed in by the React adapter. Mirrors the inputs
 *  the old `useServerData` pushed into the engine, plus the host callbacks that
 *  were previously wired separately in `useEagGridOrchestration`. */
export type GridLifecycleInputs = {
  /** Data-fetch + preset-mutation http (NO onApiError) — engine, virtual-preset PUT, reset.
   *  Its failures settle locally / are best-effort and must not blank the grid. */
  http: GridDataEngineInputs['http'];
  /** Cold-load http (WITH onApiError) — used ONLY by load() so a fatal mount-phase failure
   *  surfaces the error screen. Falls back to `http` when absent (tests). */
  loaderHttp?: GridDataEngineInputs['http'];
  queryModifier?: (filter: QueryFilterObject) => QueryFilterObject;
  predefinedColumns: readonly string[];
  onExternalFilterChange?: (filters: ExternalFilters) => void;
  /** @deprecated legacy callback — fired alongside onExternalFilterChange for old-grid parity. */
  onFilterChanged?: (filters: ExternalFilters) => void;
};

type GridLifecycleDeps = {
  store: EagGridStore;
  gridCode: string;
  /** Cold-load static config (was useEagGridDataLoader's params). */
  presetInstanceId?: string;
  disableAllActions?: boolean;
  hasSilentRefetching?: boolean;
  actionsAlignment?: ActionsAlignment;
};

/**
 * The subset of GridDataEngine that the lifecycle calls. The lifecycle accepts an
 * optional `engineFactory` so tests can inject a fake implementing only this surface.
 */
export type GridDataEngineLike = Pick<
  GridDataEngine,
  | 'fetchPage'
  | 'fetchAddedColumns'
  | 'retryFetch'
  | 'refetchQuery'
  | 'update'
  | 'revive'
  | 'dispose'
>;

type EngineFactory = (deps: GridLifecycleDeps, inputs: GridDataEngineInputs) => GridDataEngineLike;

const offsetOf = (state: EagGridState): number => {
  const settings = selectGridSettings(state) ?? DEFAULT_SETTINGS;
  return state.page * settings.itemsPerPage;
};

/** True while an external selection request cannot be resolved faithfully: before the
 *  definition loads, selectRowSelectMode falls back to 'NONE' (would reject
 *  selection-enabled grids); while fetchState is 'loading' state.rows still holds the
 *  OUTGOING page; and 'idle' means no data fetch has landed at all (the cold-mount
 *  fetchPage settles 'idle' when dataQuery is absent, with rows still empty) — resolving
 *  in any of these windows would target stale/empty rows or silently consume a request
 *  aimed at the incoming page. */
const isSelectionDeferred = (state: EagGridState): boolean =>
  isNil(state.definition) || state.fetchState === 'loading' || state.fetchState === 'idle';

/**
 * Order-dependent key of ALL sorted columns (visible and hidden).
 *
 * With multi-sort, users can sort hidden columns via the Sort panel, so
 * the key must include all sorted columns to trigger a refetch. The key
 * is ordered by sortIndex so that changing sort priority produces a
 * different key, while column drag-and-drop (which changes columnStates
 * array order but not sortIndex) does not.
 */
const sortKeyOf = (state: EagGridState): string =>
  computeSortModelKey(
    state.columnStates
      .filter((cs) => cs.sort != null)
      .toSorted((a, b) => (a.sortIndex ?? Infinity) - (b.sortIndex ?? Infinity))
      .map((cs) => ({columnKey: cs.key, order: cs.sort as 'ASC' | 'DESC'}))
  );

/**
 * Order-independent key of the visible column set (the `hidden !== true` predicate
 * matches `selectVisibleColumns`). Drives the delta-column fetch: enabling a
 * previously-hidden column fills in just that column's data, while reorder/resize/pin
 * leave the set unchanged.
 */
const visibleKeyOf = (state: EagGridState): string =>
  computeVisibleColumnsKey(
    state.columnStates.filter((cs) => cs.hidden !== true).map((cs) => cs.key)
  );

/**
 * Reaction registry row 1 — the main fetch trigger. True when offset, filter values,
 * the sorted-visible-column key, or grid settings changed (these are TRIGGERS only; the
 * engine reads the live values at call time). gridSettings is included so an itemsPerPage
 * change on page 0 (offset stays 0) still refetches with the new page size.
 */
const shouldRefetchPage = (state: EagGridState, prev: EagGridState): boolean =>
  offsetOf(state) !== offsetOf(prev) ||
  state.filterValues !== prev.filterValues ||
  sortKeyOf(state) !== sortKeyOf(prev) ||
  selectGridSettings(state) !== selectGridSettings(prev);

/**
 * Reaction registry row 2 — the visible columns added since the previous state. Enabling
 * a previously-hidden column fills in just its data without a full reload; pure removals
 * yield none. The `prevVisible === ''` guard skips the initial column population (the main
 * fetch performs the first load), and empty keys are filtered out.
 */
const computeAddedColumns = (state: EagGridState, prev: EagGridState): string[] => {
  const prevVisible = visibleKeyOf(prev);
  const nextVisible = visibleKeyOf(state);
  if (prevVisible === nextVisible || prevVisible === '') {
    return [];
  }
  const prevKeys = new Set(prevVisible.split(','));
  return nextVisible.split(',').filter((k) => k !== '' && !prevKeys.has(k));
};

/**
 * Reaction registry row 4 trigger — the dirty/PUT reaction fires when columnStates,
 * grid settings, queryId, or the resize flag changed.
 */
const shouldSyncDirty = (state: EagGridState, prev: EagGridState): boolean =>
  state.columnStates !== prev.columnStates ||
  selectGridSettings(state) !== selectGridSettings(prev) ||
  state.queryId !== prev.queryId ||
  state.isResizing !== prev.isResizing ||
  state.isPresetCopyInProgress !== prev.isPresetCopyInProgress;

/** Whether the summary row should be visible given the current state. */
const isSummaryRowActive = (state: EagGridState): boolean =>
  (state.definition?.behavior ?? DEFAULT_BEHAVIOR).summaryRowEnabled &&
  (selectGridSettings(state) ?? DEFAULT_SETTINGS).summaryRowVisible;

/**
 * Framework-free controller owning the GridDataEngine and all store-driven data
 * reactions for one EagGrid mount. Subscribes once to the store in `start()` and
 * dispatches every reaction from a single `onStoreChangeDispatch(state, prev)` diff —
 * replacing the choreography of effect hooks (`useServerData`, `useGridDataFetching`)
 * with one handler in one place.
 *
 * Being a plain class, none of the eag-grid React lint rules apply: it freely reads
 * mutable fields and calls `store.getState()`.
 *
 * ## Cross-render state machine & initialization order (LOAD-BEARING)
 *
 * The 12 private fields below are organized into 4 feature groups (marked by the
 * inline group banner comments on the fields). Their initialization order in start()
 * is load-bearing:
 *
 * | Phase | Field(s)              | Invariant                                              |
 * |-------|-----------------------|--------------------------------------------------------|
 * | Setup | prevQueryId           | Seed from store.queryId ('' initially) BEFORE subscribe |
 * | Setup | prevNotifiedFilters   | Seed from store.filterValues BEFORE subscribe (no notify on seed) |
 * | Sub   | unsubscribe           | onStoreChangeDispatch now fires on any store change     |
 * | Re-arm| loadAbort             | Re-arm if aborted (StrictMode); read signal LAZILY at write time |
 * | Load  | load()                | Async; its store writes are observed by the dispatcher  |
 *
 * Within syncPresetApplyAndDirty: applyPreset MUST set hasPresetBeenApplied=true and
 * seed lastVirtualPreset BEFORE applyPresetColumnsAndSort, because that setState notifies
 * subscribers SYNCHRONOUSLY (nested re-entry) — without the seed a spurious virtual-preset
 * PUT fires.
 *
 * **Do not reorder these initializations or you will break the state machine.**
 */
export class GridLifecycle {
  private engine: GridDataEngineLike;
  private inputs: GridLifecycleInputs;
  private unsubscribe: (() => void) | null = null;
  private refetchListener: ((e: Event) => void) | null = null;
  private applyQueryListener: ((e: CustomEvent<ApplyEagridQueryCustomEvent>) => void) | null = null;
  private selectRowsListener: ((e: CustomEvent<SelectEagridRowsCustomEvent>) => void) | null = null;
  /** External selection requests queued while the grid cannot resolve them yet
   *  (definition still loading or a data fetch in flight) — see onSelectRowsEvent.
   *  A queued 'set' request supersedes everything queued before it (it fully replaces
   *  the selection anyway), which also bounds the queue for the dominant request kind. */
  private pendingSelectRows: SelectDatagridRowsTarget[] = [];
  /** state.rows reference captured when the queue arms (empty → non-empty). The flush
   *  refuses to drain until a fetch actually LANDED rows (setRows always stores a fresh
   *  array): the engine's finally safety net can settle 'success' after an unexpected
   *  throw while keeping the outgoing page's rows, and draining then would target
   *  exactly the stale rows the deferral exists to avoid. */
  private pendingSelectRowsBaselineRows: RowData[] | null = null;
  private started = false;
  private lastStaticFiltersKey: string;

  // --- page-URL sync (absorbed from the old usePageUrlSync hook) ---
  // `hydrated` flips true once BOTH parts of the initial query state have landed and the
  // resulting data fetch has settled: queryId is real, filterValues were hydrated from
  // dataQuery, and any queryModifier normalization has finished minting its replacement ID.
  // Until then every filter/queryId change is initialization and must NOT reset the page.
  // `prevQueryId` is updated on EVERY onStoreChangeDispatch so a later preset switch
  // (truthy → truthy) is distinguishable from the initial resolution.
  private hydrated = false;
  private hasInitialQueryId = false;
  private hasInitialFilterValues = false;
  private prevQueryId = '';

  // --- summary row staleness guard ---
  private summaryRowFetchVersion = 0;

  // --- preset sync (absorbed from the old usePresetSync hook) ---
  // `lastVirtualPreset` mirrors the hook's lastVirtualPresetState ref: the last
  // virtual-preset payload that was PUT (or seeded on preset-apply). The dirty/PUT
  // reaction compares the freshly-derived preset against this to skip redundant PUTs.
  // `hasPresetBeenApplied` mirrors the hook's hasPresetBeenApplied ref: it gates the
  // dirty/PUT reaction so the columnStates writes during initial store setup (and the
  // nested onStoreChangeDispatch from applyPresetColumnsAndSort) do not trigger a spurious PUT.
  private lastVirtualPreset: VirtualPreset | null = null;
  private hasPresetBeenApplied = false;

  // --- cold load (absorbed from the old useEagGridDataLoader hook) ---
  // Abort controller for the cold-mount load chain. The chain reads `signal.aborted`
  // LAZILY (at write time, not capture time) so it sees the re-armed controller after a
  // StrictMode simulated unmount, and the genuinely aborted one after a real unmount.
  // start() re-arms it (if aborted) before calling load(); dispose() aborts it. This
  // mirrors the hook's abortControllerRef + the separate StrictMode re-arm effect.
  private loadAbort = new AbortController();

  // --- host filter-change notification (absorbed from the old useEagGridOrchestration) ---
  // Mirrors the hook's prevFilterValuesForNotificationRef: seeded in start() with the mount
  // filterValues so the seed does NOT notify, then updated on every filterValues ref change
  // that fires the host callback. The store default is `{}` (never null); the `| null` only
  // marks the pre-start() state before the seed is taken.
  private prevNotifiedFilters: Filters | null = null;

  constructor(
    private deps: GridLifecycleDeps,
    initialInputs: GridLifecycleInputs,
    // Test-only seam: defaults to a real GridDataEngine. Tests inject a fake
    // implementing GridDataEngineLike to assert the lifecycle's fetch triggers in
    // isolation from the engine's network mechanics.
    engineFactory: EngineFactory = (deps, inputs) => new GridDataEngine(deps, inputs)
  ) {
    this.inputs = initialInputs;
    this.engine = engineFactory(
      {store: deps.store, gridCode: deps.gridCode},
      {
        http: initialInputs.http,
        queryModifier: initialInputs.queryModifier,
        predefinedColumns: initialInputs.predefinedColumns,
      }
    );
    this.lastStaticFiltersKey = JSON.stringify(initialInputs.queryModifier?.({}) ?? {});
  }

  /**
   * Push per-render volatile inputs. Mirrors `useServerData`'s engine.update plus a
   * prop-driven static-filter trigger: the queryModifier output is NOT in the store,
   * so a change there cannot arrive via `store.subscribe` — nudge a fetch explicitly
   * (only once started, matching the old fetch-effect's staticFiltersKey dependency).
   */
  update(inputs: GridLifecycleInputs): void {
    this.inputs = inputs;
    this.engine.revive();
    this.engine.update({
      http: inputs.http,
      queryModifier: inputs.queryModifier,
      predefinedColumns: inputs.predefinedColumns,
    });
    const nextStaticKey = JSON.stringify(inputs.queryModifier?.({}) ?? {});
    if (this.started && nextStaticKey !== this.lastStaticFiltersKey) {
      this.lastStaticFiltersKey = nextStaticKey;
      this.engine.fetchPage(offsetOf(this.deps.store.getState()));
    } else {
      this.lastStaticFiltersKey = nextStaticKey;
    }
  }

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    // The page cache is module-level, so it survives navigation away from the grid. Data
    // mutated while the grid was unmounted (detail-page edits, refreshDatagrid dispatched
    // with no listener) would otherwise be served stale for up to the cache TTL on
    // remount. Clearing here scopes cache hits to a single mounted grid session —
    // pagination back-and-forth stays instant, a fresh mount always refetches.
    clearPageDataCacheForGrid(this.deps.gridCode);

    // --- page restore (old usePageUrlSync Phase 1) ---
    // Runs in the adapter's useLayoutEffect, BEFORE subscribe and BEFORE the initial
    // mount fetch below — so the restored offset is in the store before any fetch reads
    // it. This eliminates the old useLayoutEffect-ordering hack (usePageUrlSync no longer
    // has to run before the fetch effect). URL page is 1-based; store is 0-based.
    const urlPage = getPageFromUrl(this.deps.gridCode);
    if (urlPage !== undefined && urlPage >= 1) {
      this.deps.store.setPage(urlPage - 1);
    }

    // Advertise the current (restored or default) page in the URL on mount, even when it did
    // not change. Mirrors the old usePageUrlSync Phase 2's initial run: syncPageUrl below only
    // writes on a page CHANGE, so without this the param is never written on a fresh load (no
    // `&page=` in the URL) and a missing param stays missing. 1-based URL; 0-based store.
    setPageInUrl(this.deps.gridCode, this.deps.store.getState().page + 1);

    // Seed prevQueryId from the live store so the first onStoreChangeDispatch diff is correct
    // (mirrors the hook seeding prevQueryIdRef from its initial params.queryId).
    this.prevQueryId = this.deps.store.getState().queryId;
    this.hasInitialQueryId = this.prevQueryId !== '';

    // Seed the host-notification baseline with the mount filterValues so the seed values
    // do not notify (mirrors the hook's first-render seed of prevFilterValuesForNotificationRef).
    this.prevNotifiedFilters = this.deps.store.getState().filterValues;

    this.unsubscribe = this.deps.store.subscribe((state, prev) =>
      this.onStoreChangeDispatch(state, prev)
    );
    this.refetchListener = (e: Event) => this.onRefetchEvent(e);
    window.addEventListener(REFETCH_EAGRID_EVENT, this.refetchListener);
    this.applyQueryListener = (e) => {
      void this.onApplyQueryEvent(e);
    };
    window.addEventListener(APPLY_EAGRID_QUERY_EVENT, this.applyQueryListener);
    this.selectRowsListener = (e) => this.onSelectRowsEvent(e);
    window.addEventListener(SELECT_EAGRID_ROWS_EVENT, this.selectRowsListener);

    // --- cold load (old useEagGridDataLoader, run on mount) ---
    // Re-arm the abort controller after a StrictMode simulated unmount: dispose() aborted
    // it, but on the immediate re-start the load chain must see a live controller again
    // (the chain reads the signal lazily). Mirrors the hook's StrictMode re-arm effect.
    // load() runs AFTER subscribe so its async store writes (definition/activePreset/
    // dataQuery/queryId) are observed by onStoreChangeDispatch and drive the preset-apply, page
    // reset, host notification and first real fetch reactions — exactly as the hook's
    // writes drove the old effect-based fetch. load() itself only KICKS OFF promises
    // synchronously and returns; the writes land in later microtasks, so the synchronous
    // initial mount fetch below still fires first (same ordering as before this move).
    if (this.loadAbort.signal.aborted) {
      this.loadAbort = new AbortController();
    }
    this.load();

    // Initial mount fetch. Reproduces the old `useGridDataFetching` main effect firing
    // once on mount (its [offset, filterValues, ...] deps ran on first commit). The
    // adapter starts the lifecycle in a useLayoutEffect AFTER any page-URL restore, so
    // this reads the already-restored offset — a single fetch on mount, not a double.
    // When dataQuery is not yet present (cold load still pending) the engine settles to
    // 'idle' and the loader's later store writes re-trigger the fetch via onStoreChangeDispatch.
    this.engine.fetchPage(offsetOf(this.deps.store.getState()));
  }

  dispose(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    if (this.refetchListener) {
      window.removeEventListener(REFETCH_EAGRID_EVENT, this.refetchListener);
      this.refetchListener = null;
    }
    if (this.applyQueryListener) {
      window.removeEventListener(APPLY_EAGRID_QUERY_EVENT, this.applyQueryListener);
      this.applyQueryListener = null;
    }
    if (this.selectRowsListener) {
      window.removeEventListener(SELECT_EAGRID_ROWS_EVENT, this.selectRowsListener);
      this.selectRowsListener = null;
    }
    this.dropPendingSelectRows();
    // Abort the cold-load chain (mirrors the hook's abort-on-unmount cleanup). On a real
    // unmount this prevents post-unmount store writes; on a StrictMode simulated unmount the
    // next start() re-arms a fresh controller so the still-running chain commits its results.
    this.loadAbort.abort();
    this.engine.dispose();
    // Clear `started` so a StrictMode simulated unmount/remount (which reuses the same
    // lifecycle instance) re-subscribes on the next start(). The engine is independently
    // re-armed via revive() in update(). Mirrors the old useServerData revive() pattern.
    this.started = false;
  }

  /**
   * Cold-mount loading sequence, absorbed VERBATIM from the old useEagGridDataLoader hook
   * (its useOnMount callback body). Fires every request whose inputs are known at mount in
   * parallel, then dispatches dependent requests as their inputs land, writing results to
   * the store via setters. Returns void — all observable state lives in the store, where
   * onStoreChangeDispatch observes the writes and drives the dependent reactions.
   *
   * Called once from start() AFTER subscribe is established. Reads `loadAbort.signal.aborted`
   * lazily at each write site (the INVARIANT below) so it sees the re-armed controller after
   * a StrictMode simulated unmount and the aborted one after a real unmount.
   */
  private load(): void {
    const store = this.deps.store;
    // Use the onApiError-wired cold-load http so a fatal mount-phase failure surfaces the
    // error screen (engine/preset use the plain `http`, whose failures must NOT blank the grid).
    const http = this.inputs.loaderHttp ?? this.inputs.http;
    const gridCode = this.deps.gridCode;

    // Read the signal lazily (at check time, not capture time) so the chain sees the
    // re-armed controller after a StrictMode simulated unmount, and the genuinely
    // aborted one after a real unmount.
    // INVARIANT: every async code path that writes to the store must check
    // isAborted() immediately before the write (or before a sequence of writes).
    const isAborted = () => this.loadAbort.signal.aborted;

    store.initStaticConfig({
      gridCode,
      presetInstanceId: this.deps.presetInstanceId,
      disableAllActions: this.deps.disableAllActions,
      hasSilentRefetching: this.deps.hasSilentRefetching,
      actionsAlignment: this.deps.actionsAlignment,
    });

    // ---- Phase 1: fire every mount-time request in parallel ----
    const presetsP = http.getPresets();
    const virtualP = http.getVirtualPreset({fresh: true});
    const definitionP = http.getDefinition({loadChoiceFilterOptions: true});

    const urlQueryId = getSearchParamsByGridCode(gridCode)?.queryId;
    const urlDqP = urlQueryId ? http.getDataQuery(urlQueryId) : null;

    // Independent results land straight in the store.
    void definitionP.then((d) => {
      if (d && !isAborted()) {
        store.setDefinition(d);
      }
    });
    void virtualP.then((v) => {
      if (v && !isAborted()) {
        store.setVirtualPreset(v);
      }
    });

    const staticFilters = this.inputs.queryModifier?.({});

    const resolveFromCustomPreset = async (activeCustom: Preset): Promise<Resolution> => {
      const [detail, reconcileOrDq] = await Promise.all([
        http.getPreset(activeCustom.id),
        staticFilters
          ? reconcilePresetWithQueryModifier(
              activeCustom,
              http,
              staticFilters,
              this.loadAbort.signal
            )
          : http.getDataQuery(activeCustom.dataQueryId),
      ]);

      // When reconcile mints a new dataQueryId (filters diverged from staticFilters), the
      // detail fetched in parallel still carries the OLD dataQueryId, and reconcileOrDq.dataQuery
      // is the OLD body. Patch detail and refetch the new body to preserve correctness on the
      // slow path.
      const newId = isReconcileResult(reconcileOrDq) ? reconcileOrDq.newDataQueryId : undefined;
      const activePreset = detail ? (newId ? {...detail, dataQueryId: newId} : detail) : null; // eslint-disable-line no-nested-ternary -- Pre-existing violation

      if (urlDqP) {
        return {activePreset, dataQuery: null};
      }
      if (newId) {
        return {activePreset, dataQuery: (await http.getDataQuery(newId)) ?? null};
      }
      const dataQuery = isReconcileResult(reconcileOrDq)
        ? reconcileOrDq.dataQuery
        : (reconcileOrDq ?? null);
      return {activePreset, dataQuery};
    };

    const resolveFromVirtualPreset = async (): Promise<Resolution> => {
      const virtualPreset = await virtualP;
      if (!virtualPreset) {
        return {activePreset: null, dataQuery: null};
      }
      if (urlDqP) {
        return {activePreset: virtualPreset, dataQuery: null};
      }
      return {
        activePreset: virtualPreset,
        dataQuery: (await http.getDataQuery(virtualPreset.dataQueryId)) ?? null,
      };
    };

    // URL queryId overrides the preset's dataQuery. urlDqP was fired at t=0; on failure
    // we fall back to the active preset's stored dataQuery and rewrite the URL.
    const resolveUrlOverride = async (
      activePreset: PresetDetail | VirtualPreset | null
    ): Promise<GetDataQueryResponse | null> => {
      if (!urlDqP) {
        return null;
      }
      return (
        (await urlDqP.catch(async () => {
          if (!activePreset) {
            return null;
          }
          setSearchParamsByGridCode(gridCode, activePreset.dataQueryId);
          return (await http.getDataQuery(activePreset.dataQueryId)) ?? null;
        })) ?? null
      );
    };

    // ---- Phase 2: presets drive the active preset + initial dataQuery ----
    const loadActivePresetAndData = async () => {
      const presets = await presetsP;
      if (!presets || isAborted()) {
        return;
      }
      store.setCustomPresets(presets);
      this.migrateLegacyPresetPrefs(presets, isAborted);

      const activeCustom = presets.find((p) => p.isActive);

      // Best-effort reconciliation for non-active presets; silent on failure.
      // When reconciliation mints a new dataQueryId (PUT to the BE), the store's
      // customPresets entry must be repointed too — a stale list id would flow into
      // the next activation (queryId/URL/row-count reads), diverging from the
      // authoritative preset detail (T20-134447).
      if (staticFilters) {
        for (const preset of presets) {
          if (preset.id === activeCustom?.id) {
            continue;
          }
          reconcilePresetWithQueryModifier(preset, http, staticFilters, this.loadAbort.signal)
            .then((result) => {
              const newDataQueryId = result?.newDataQueryId;
              if (!newDataQueryId || isAborted()) {
                return;
              }
              const currentPresets = store.getState().customPresets;
              if (!currentPresets) {
                return;
              }
              store.setCustomPresets(
                currentPresets.map((p) =>
                  p.id === preset.id ? {...p, dataQueryId: newDataQueryId} : p
                )
              );
            })
            .catch(() => {});
        }
      }

      const {activePreset, dataQuery: presetDataQuery} = activeCustom
        ? await resolveFromCustomPreset(activeCustom)
        : await resolveFromVirtualPreset();

      const dataQuery = urlDqP ? await resolveUrlOverride(activePreset) : presetDataQuery;

      if (isAborted()) {
        return;
      }

      if (activePreset) {
        store.setActivePreset(activePreset);
      }
      if (dataQuery) {
        store.setDataQuery(dataQuery);
        // Seed queryId from the loaded query so the custom-preset dirty check has a correct
        // baseline on first render. Without this, store.queryId stays '' until the first data
        // fetch syncs it, making an unmodified custom preset render a spurious "* ".
        store.setQueryId(dataQuery.id);
        if (isNilOrEmpty(getSearchParamsByGridCode(gridCode))) {
          setSearchParamsByGridCode(gridCode, dataQuery.id);
        }
      }
    };

    void loadActivePresetAndData();
  }

  private migrateLegacyPresetPrefs(presets: Preset[], isAborted: () => boolean): void {
    void migratePresetLocalPrefs(this.deps.gridCode, presets, this.inputs.http)
      .then((migrated) => {
        if (isAborted() || migrated.every((preset, index) => preset === presets[index])) {
          return;
        }
        const current = this.deps.store.getState().customPresets;
        if (isNil(current)) {
          return;
        }
        const originalById = new Map(presets.map((preset) => [preset.id, preset]));
        const migratedById = new Map(migrated.map((preset) => [preset.id, preset]));
        this.deps.store.setCustomPresets(
          current.map((preset) => {
            const original = originalById.get(preset.id);
            const updated = migratedById.get(preset.id);
            if (isNil(original) || isNil(updated)) {
              return preset;
            }
            return {
              ...preset,
              ...(preset.color === original.color ? {color: updated.color} : {}),
              ...(preset.isPinned === original.isPinned ? {isPinned: updated.isPinned} : {}),
            };
          })
        );
      })
      .catch(() => {
        // Preference migration is best-effort; a failure must not affect grid loading.
      });
  }

  /** Imperative refresh exposed to the view via the adapter (bulk ops, row actions).
   *  Clears the page cache then refetches the CURRENT query at the live offset —
   *  refetchQuery (not fetchPage) so the pagination fast path cannot skip the /count
   *  a mutation may have changed. Also refetches summary row. */
  refresh(): void {
    clearPageDataCacheForGrid(this.deps.gridCode);
    this.engine.refetchQuery(offsetOf(this.deps.store.getState()));
    const state = this.deps.store.getState();
    if (isSummaryRowActive(state) && state.queryId) {
      this.fetchSummaryRow(state.queryId);
    }
  }

  /** Retry a failed data/count fetch at the live offset, forcing the /count refetch
   *  (see GridDataEngine.retryFetch). Exposed to the view via the adapter — drives the
   *  content-area error state's retry button. */
  retryFetch(): void {
    this.engine.retryFetch(offsetOf(this.deps.store.getState()));
  }

  /**
   * Reset columns to the server default (absorbed from the old useEagGridOrchestration's
   * handleResetColumns): DELETE the virtual preset, then apply the fresh preset's columns.
   * `startFetch()` shows the loading bar immediately — `applyPresetColumnsAndSort` only
   * updates column state and does not always trigger a data refetch, so without it the bar
   * might never appear. When DELETE returns no fresh preset, settle the fetch to 'success'.
   *
   * The explicit `fetchPage` after the apply is what SETTLES that loading state: the apply
   * re-triggers the main fetch reaction only when it changes the sort model. A reorder /
   * visibility-only reset mutates just `columnStates`, whose only data reaction is the
   * delta-column fetch — and that deliberately never touches fetch state — so without the
   * explicit fetch the grid would stay in 'loading' forever (T20-134772). The fetch reads
   * the post-apply state (restored column set), reuses the live queryId, and always settles
   * via the engine's finally safety net. If the apply DID start a reaction-driven fetch,
   * this call aborts and supersedes it (standard abort-controller owner transfer).
   *
   * `lastVirtualPreset` MUST be reseeded from the fresh preset BEFORE the apply (mirrors
   * applyPreset): the apply also reorders columnStates to the preset index, so its nested
   * dirty/PUT reaction would compare the applied default against the stale (e.g. dragged)
   * baseline and PUT-recreate the virtual preset the DELETE just removed — silently undoing
   * the reset server-side. The seed uses the same shape the dirty reaction derives: clamped
   * lockedHidden visibility, the CURRENT working gridSettings (reset does not touch them),
   * and the live search-param queryId.
   *
   * The `started` guard drops a DELETE that resolves after dispose() (mirrors
   * onApplyQueryEvent): the continuation would otherwise start a fresh fetch chain for the
   * dead grid and rewrite the URL of whatever page the user navigated to.
   */
  resetColumns(): void {
    this.deps.store.startFetch();
    this.inputs.http.deleteVirtualPreset().then((freshPreset) => {
      if (!this.started) {
        return;
      }
      if (freshPreset) {
        const state = this.deps.store.getState();
        this.lastVirtualPreset = {
          columnsSettings: filterTechnicalArray(
            clampSettingsToLockedHidden(freshPreset.columnsSettings, state.columnStates)
          ),
          gridSettings: stripLocalGridSettings(selectGridSettings(state) ?? DEFAULT_SETTINGS),
          dataQueryId:
            getSearchParamsByGridCode(this.deps.gridCode)?.queryId ?? freshPreset.dataQueryId,
        };
        this.deps.store.applyPresetColumnsAndSort(freshPreset.columnsSettings);
        this.engine.fetchPage(offsetOf(this.deps.store.getState()));
      } else {
        this.deps.store.settleFetch('success');
      }
    });
  }

  /**
   * Host filter-change notification (absorbed from the old useEagGridOrchestration effect).
   * Notifies the host on any filterValues ref change — preset switch, URL hydration, or user
   * filter action — after the initial seed taken in start() (so the mount values do not
   * notify). Reads `onExternalFilterChange` from this.inputs at call time since the adapter
   * pushes it in via update() every render. Also fires the deprecated `onFilterChanged`
   * callback with the same unwrapped values, subject to the same skip-on-seed behavior —
   * both callbacks are notified together, for old-grid parity.
   */
  private notifyHost(state: EagGridState): void {
    if (state.filterValues === this.prevNotifiedFilters) {
      return;
    }
    this.prevNotifiedFilters = state.filterValues;
    this.inputs.onExternalFilterChange?.(unwrapFilterValuesRaw(state.filterValues));
    this.inputs.onFilterChanged?.(unwrapFilterValuesRaw(state.filterValues));
  }

  /**
   * Central store-change dispatcher — the single hub orchestrating every store-driven
   * reaction for this grid mount. Registered once in start() via store.subscribe; runs
   * synchronously on every store mutation (Zustand vanilla notifies subscribers SYNC).
   *
   * ## Reaction registry (trigger → effect)
   *
   * | # | Trigger (state vs prev)                                   | Effect                                  |
   * |---|-----------------------------------------------------------|-----------------------------------------|
   * | 1 | offset \| filterValues \| sortModelKey \| gridSettings    | engine.fetchPage(offset)                |
   * | 2 | visible-column set gained a column                        | engine.fetchAddedColumns(added, offset) |
   * | 3 | page change + hydration boundary                          | syncPageUrl (write &page=, reset on hydration) |
   * | 4 | activePreset.columnsSettings \| columnStates \| queryId   | syncPresetApplyAndDirty (apply + dirty/PUT) |
   * | 5 | queryId changed \| summaryRowVisible toggled               | syncSummaryRow (fetch or clear)         |
   * | 6 | filterValues ref change                                   | notifyHost (onExternalFilterChange)     |
   *
   * Reactions may trigger NESTED store mutations (applyPresetColumnsAndSort, setPage) that
   * re-enter this dispatcher synchronously. The nested re-entry is guarded by ordered
   * flags (hasPresetBeenApplied, lastVirtualPreset, hydrated, prevQueryId) set in a precise
   * order — see syncPresetApplyAndDirty / syncPageUrl JSDoc. Do NOT reorder the reactions:
   * the sequence (fetch → delta-columns → page-url → preset → notify) has natural
   * dependencies and the nested-mutation guards assume this order.
   */
  private onStoreChangeDispatch(state: EagGridState, prev: EagGridState): void {
    // offset is reused by both the main fetch and the delta-column fetch below.
    const offset = offsetOf(state);

    // Row 1 — main fetch.
    if (shouldRefetchPage(state, prev)) {
      this.engine.fetchPage(offset);
    }

    // Row 2 — delta column fetch (additions only).
    const added = computeAddedColumns(state, prev);
    if (added.length > 0) {
      this.engine.fetchAddedColumns(added, offset);
    }

    this.syncPageUrl(state, prev);
    this.syncPresetApplyAndDirty(state, prev);
    this.syncSummaryRow(state, prev);
    this.notifyHost(state);
    this.flushPendingSelectRows();
  }

  /**
   * Preset sync, absorbed verbatim from the old usePresetSync hook (its two effects):
   *
   * **(a) Preset-apply** — when `activePreset.columnsSettings` reference changes
   *   (preset activation/update), apply columns+sort atomically via
   *   `applyPresetColumnsAndSort(cols)` (cols = columnsSettings ?? [] — run even when
   *   empty; load-bearing for `isColumnsReady`), flag `hasPresetBeenApplied`, and for
   *   non-custom presets SEED `lastVirtualPreset` so the dirty/PUT reaction sees no diff
   *   and skips the PUT.
   *
   * **(b) Dirty/PUT** — when `columnStates` / `gridSettings` / `queryId` / `isResizing`
   *   change: bail until applied; bail while resizing; for a custom preset set the dirty
   *   flag (no PUT); for a non-custom (virtual) preset PUT the modified preset unless it
   *   is invalid or unchanged.
   *
   * NESTED-SETSTATE ORDERING (subscribe model): unlike React's batched commit the hook
   * relied on, `applyPresetColumnsAndSort`'s `setState` notifies subscribers SYNCHRONOUSLY
   * — it re-enters `onStoreChangeDispatch` nested, BEFORE the apply block below returns. To keep
   * the hook's no-spurious-PUT guarantee we therefore set `hasPresetBeenApplied = true`
   * and seed `lastVirtualPreset` BEFORE calling `applyPresetColumnsAndSort`, so the nested
   * onStoreChangeDispatch's dirty/PUT branch sees the seed (→ isVirtualPresetEqual true → no PUT).
   * The nested call does NOT re-run the apply branch (activePreset.columnsSettings is
   * unchanged by applyPresetColumnsAndSort), so there is no infinite loop.
   *
   * INVARIANT — DO NOT SPLIT: Reactions (a) preset-apply and (b) dirty/PUT MUST run in
   * order within the SAME onStoreChangeDispatch call so they share one (state, prev)
   * snapshot. Splitting them into separate store subscribers breaks the nested re-entrance
   * guarantee: a second subscriber would observe stale (state, prev) or fire mid-apply
   * (columnStates half-updated), reintroducing spurious PUTs. A future split would first
   * need to move the seeding into a shared pre-dispatch step or use promise-based gates.
   *
   * STALE-SNAPSHOT GUARD: when (a) actually applied a preset, (b) is SKIPPED for this
   * dispatch. `applyPresetColumnsAndSort`'s nested onStoreChangeDispatch already ran the
   * dirty/PUT reaction on the POST-apply state; running (b) here as well would recompute
   * on THIS dispatch's PRE-apply snapshot (old columnStates + new activePreset) and
   * overwrite the fresh result — visible as a transient spurious "* " on a just-activated
   * custom preset until the activation PUT settles (T20-134447). The trigger is real on
   * every preset switch: setActivePreset always reseeds `gridSettings` (a fresh HTTP
   * object), so shouldSyncDirty fires on the same dispatch that applied the preset.
   */
  private syncPresetApplyAndDirty(state: EagGridState, prev: EagGridState): void {
    // (a) Preset-apply: columnsSettings reference changed.
    if (state.activePreset?.columnsSettings !== prev.activePreset?.columnsSettings) {
      this.applyPreset(state);
      if (state.activePreset) {
        // Applied → the nested dispatch handled dirty/PUT on fresher state (see
        // STALE-SNAPSHOT GUARD above). Only a null activePreset falls through:
        // applyPreset no-ops then, so no nested dispatch ran.
        return;
      }
    }

    // (b) Dirty/PUT: columnStates | gridSettings | queryId | isResizing changed.
    if (shouldSyncDirty(state, prev)) {
      this.syncDirtyOrPutPreset(state);
    }
  }

  /** Reaction (a): apply the active preset's columns+sort and seed the PUT baseline. */
  private applyPreset(state: EagGridState): void {
    const activePreset = state.activePreset;
    if (!activePreset) {
      return;
    }

    // Always run applyPresetColumnsAndSort once the active preset is known — even if
    // columnsSettings is empty. Without this, isColumnsReady stays false forever for
    // grids whose preset has no column overrides (driven entirely by predefinedColumns).
    const cols = activePreset.columnsSettings ?? [];
    const settings = selectGridSettings(state) ?? DEFAULT_SETTINGS;

    // Seed BEFORE applyPresetColumnsAndSort so the nested onStoreChangeDispatch its setState fires
    // sees the seed and skips the PUT (see syncPresetApplyAndDirty JSDoc). Mirrors the hook's seed,
    // which prevented a spurious PUT in React's batched commit.
    this.hasPresetBeenApplied = true;
    if (!isPresetCustom(activePreset)) {
      // Seed the dataQueryId with the value the LIVE search param will hold once the cold load
      // finishes. applyPreset runs on setActivePreset, which the cold load fires BEFORE it writes
      // the search param — so here getSearchParamsByGridCode is usually still empty. Falling back
      // to '' (rather than the preset's own dataQueryId) made the later queryId-change reaction see
      // a dataQueryId diff vs the now-written search param and PUT spuriously on initial load. The
      // active preset's dataQueryId IS the id the cold load writes to the search param, so it is the
      // correct baseline; a genuine later query-mint still differs from it and PUTs as intended.
      const seedQueryId =
        getSearchParamsByGridCode(this.deps.gridCode)?.queryId ?? activePreset.dataQueryId;
      // Clamp visible:false for lockedHidden columns — applyPresetColumnsAndSort force-hides
      // them, so an unclamped seed built from a stale preset (visible: true) would differ
      // from the applied state and fire a spurious PUT on the nested dispatch.
      this.lastVirtualPreset = {
        columnsSettings: filterTechnicalArray(
          clampSettingsToLockedHidden(cols, state.columnStates)
        ),
        gridSettings: stripLocalGridSettings(settings),
        dataQueryId: seedQueryId,
      };
    }

    this.deps.store.applyPresetColumnsAndSort(cols);
  }

  /**
   * Reaction (b): compute and set the custom-preset dirty flag, or PUT the virtual preset.
   *
   * @mandatory CUSTOM PRESET DIRTY-STATE CONTRACT:
   * - GridLifecycle (this method) is the SOLE SOURCE OF TRUTH for computing isDirty via
   *   isCustomPresetDirty(). It observes store changes (columnStates/gridSettings/queryId)
   *   and sets store.isPresetChanged whenever current state differs from the saved preset.
   * - usePresetActivation resets isPresetChanged to false AFTER successful async operations
   *   (activate/deactivate) complete — post-mutation cleanup, NOT a second source of truth.
   * - UI (PresetPanel, ViewDiffControls, PresetButton) consumes isPresetChanged to render
   *   the "unsaved changes" indicator and action buttons.
   * - Invariant: isPresetChanged is true iff the active custom preset has unsaved user edits.
   * - Ordering invariant: callers activating a preset MUST setQueryId(dataQueryId) BEFORE
   *   setActivePreset(...) (see hooks/applyPresetQuerySync.ts), else the queryId leg
   *   below reads stale and reports a spurious dirty "*".
   */
  private syncDirtyOrPutPreset(state: EagGridState): void {
    // Do not PUT during initial load — wait until the active preset has been applied.
    if (!this.hasPresetBeenApplied || state.isPresetCopyInProgress) {
      return;
    }

    // Skip while the user is actively resizing a column (optimistic local width already
    // shown). When isResizing flips false this reaction re-runs and sends a single PUT.
    if (state.isResizing) {
      return;
    }

    const activePreset = state.activePreset;
    const settings = selectGridSettings(state) ?? DEFAULT_SETTINGS;

    if (activePreset && isPresetCustom(activePreset)) {
      const savedPreset = activePreset as PresetDetail;
      const currentColumns = filterTechnicalArray(getPresetStates(state.columnStates));

      const isDirty = isCustomPresetDirty(
        {
          // Clamp visible:false for lockedHidden columns: the store force-hides them on
          // apply, so a stale saved visible:true must not read as an unsaved user edit.
          columnsSettings: filterTechnicalArray(
            clampSettingsToLockedHidden(savedPreset.columnsSettings, state.columnStates)
          ),
          // activePreset.gridSettings is the SAVED baseline: setGridSettings writes the
          // top-level working copy and never touches the preset object.
          gridSettings: stripLocalGridSettings(savedPreset.gridSettings),
          queryId: savedPreset.dataQueryId,
        },
        {
          columnsSettings: currentColumns,
          gridSettings: stripLocalGridSettings(settings),
          queryId: state.queryId ?? '',
        }
      );

      this.deps.store.setIsPresetChanged(isDirty);
      return;
    }

    const dataQueryId = getSearchParamsByGridCode(this.deps.gridCode)?.queryId;
    if (!dataQueryId) {
      return;
    }

    const currentColumnSettings = filterTechnicalArray(getPresetStates(state.columnStates));

    const modifiedPreset: VirtualPreset = {
      columnsSettings: currentColumnSettings,
      gridSettings: stripLocalGridSettings(settings),
      // Use the LIVE search-param queryId, not the seeded lastVirtualPreset id. The engine
      // (sole-writer handoff, Task 5) mints a fresh dataQueryId and writes it to the search
      // param BEFORE setQueryId fires this reaction; reading it here is what persists the
      // newly-minted query into the virtual preset. Falling back to the stale seed would
      // PUT the OLD id (and isVirtualPresetEqual would then skip the PUT entirely),
      // silently dropping the new query — the documented double-writer corruption.
      dataQueryId,
    };

    if (isPresetColumnsSettingsInvalid(modifiedPreset)) {
      return;
    }

    const isPresetModified = isNilOrEmpty(this.lastVirtualPreset)
      ? true
      : !isVirtualPresetEqual(this.lastVirtualPreset!, modifiedPreset);

    if (!isPresetModified) {
      return;
    }

    this.lastVirtualPreset = modifiedPreset;
    this.inputs.http.updateVirtualPreset(modifiedPreset)?.catch(() => {
      // Silent catch — column/settings sync is best-effort.
    });
  }

  /**
   * Page-URL sync, absorbed verbatim from the old usePageUrlSync hook:
   *
   * - **Write (Phase 2):** on a page change, write the 1-based page to `&page=` via
   *   `setPageInUrl`. Uses `state.page` (the live post-change value in this subscriber)
   *   rather than any stale snapshot — equivalent to the hook's `store.getState().page`.
   *
   * - **Reset (Phase 3):** once hydrated (initial queryId + filterValues resolved and their
   *   fetch settled), reset page to 0 when `filterValues` ref changed OR (`queryId` changed
   *   AND prev queryId was truthy).
   *   The `setPage(0)` re-enters `onStoreChangeDispatch` (nested subscribe round) which drives the
   *   offset-0 fetch; `store.setPage` no-ops when page is already 0, so no infinite loop.
   *
   * `prevQueryId` is updated on every call (both the not-yet-hydrated and hydrated paths),
   * mirroring the hook's `prevQueryIdRef`.
   */
  private syncPageUrl(state: EagGridState, prev: EagGridState): void {
    // Phase 2: write page to URL on page change.
    if (state.page !== prev.page) {
      setPageInUrl(this.deps.gridCode, state.page + 1);
    }

    // Phase 3: reset page on filter / preset change after hydration.
    const filtersChanged = state.filterValues !== prev.filterValues;
    const presetChanged = state.queryId !== this.prevQueryId && Boolean(this.prevQueryId);

    if (!this.hydrated) {
      // Cold load resolves queryId before useFilterHydration seeds filterValues. That seed can
      // start a queryModifier fetch which mints another queryId, so hydration closes only on a
      // later terminal fetch-state dispatch, never on the filter-change dispatch itself.
      if (state.queryId !== '' && this.prevQueryId === '') {
        this.hasInitialQueryId = true;
      }
      if (filtersChanged) {
        this.hasInitialFilterValues = true;
      }
      const hasInitialFetchSettled = state.fetchState === 'success' || state.fetchState === 'error';
      this.hydrated =
        this.hasInitialQueryId &&
        this.hasInitialFilterValues &&
        !filtersChanged &&
        hasInitialFetchSettled;
      this.prevQueryId = state.queryId;
      return;
    }

    if (filtersChanged || presetChanged) {
      this.deps.store.setPage(0);
    }

    this.prevQueryId = state.queryId;
  }

  /**
   * Reaction 5: fetch or clear the summary row.
   *
   * Fetches when:
   * - queryId changed to a non-empty value AND summary row is active (enabled + visible)
   * - summaryRowVisible toggled to true AND queryId is available AND summaryRowEnabled
   *
   * Clears when summary row becomes inactive (disabled or hidden).
   */
  private syncSummaryRow(state: EagGridState, prev: EagGridState): void {
    const active = isSummaryRowActive(state);
    const wasActive = isSummaryRowActive(prev);
    const queryIdChanged = state.queryId !== prev.queryId && state.queryId !== '';

    // state.queryId must be non-empty even on the became-active branch: on reload with a
    // persisted summaryRowVisible=true, gridSettings hydrate BEFORE the engine sets queryId,
    // and fetching GET /data//summary with an empty id is a malformed request. The later
    // '' → real queryId transition re-enters via queryIdChanged and fetches then.
    if (active && state.queryId && (queryIdChanged || !wasActive)) {
      this.fetchSummaryRow(state.queryId);
      return;
    }

    if (wasActive && !active) {
      this.deps.store.setSummaryRowCells(null);
    }
  }

  private fetchSummaryRow(queryId: string): void {
    const version = ++this.summaryRowFetchVersion;
    this.inputs.http
      .getSummaryRow(queryId)
      .then((cells) => {
        if (version !== this.summaryRowFetchVersion) {
          return;
        }
        if (cells) {
          this.deps.store.setSummaryRowCells(cells as Record<string, CellsV5>);
        }
      })
      .catch(() => {
        if (version !== this.summaryRowFetchVersion) {
          return;
        }
        this.deps.store.setSummaryRowCells(null);
      });
  }

  private async onApplyQueryEvent(e: CustomEvent<ApplyEagridQueryCustomEvent>): Promise<void> {
    if (e.detail.gridCode !== this.deps.gridCode) {
      return;
    }

    // Snapshot the query so a preset switch / second apply landing mid-fetch isn't reverted below.
    const queryIdAtDispatch = this.deps.store.getState().queryId;

    // A null or rejected lookup must not blank the grid — keep the current query, no revert.
    const dataQuery = await this.inputs.http.getDataQuery(e.detail.dataQueryId).catch(() => null);
    if (!dataQuery || !this.started || this.deps.store.getState().queryId !== queryIdAtDispatch) {
      // Nothing was applied (lookup failed, the grid is gone, or a newer query won the
      // race). The dispatcher is waiting on the result channel — tell it, don't hang it.
      dispatchApplyDataGridQueryResult({
        gridCode: this.deps.gridCode,
        dataQueryId: e.detail.dataQueryId,
        requestId: e.detail.requestId,
        outcome: 'failed',
      });
      return;
    }

    setSearchParamsByGridCode(this.deps.gridCode, e.detail.dataQueryId);
    // queryId BEFORE dataQuery — load-bearing ordering from applyPresetQuerySync (dirty check).
    this.deps.store.setQueryId(e.detail.dataQueryId);
    this.deps.store.setDataQuery(dataQuery);

    // setDataQuery does NOT itself start the fetch: the refetch comes from the useFilterHydration
    // effect, a render later. The ack is still safe because waitForFetchSettle subscribes to the
    // store synchronously here — before any effect can run — so it cannot miss the
    // 'loading' → settled transition that follows.
    const settled = await waitForFetchSettle(this.deps.store, APPLY_QUERY_FETCH_SETTLE_TIMEOUT_MS);

    // The grid can be torn down while we wait. A disposed lifecycle has no apply left to report,
    // and a late 'failed' would overwrite whatever the next grid is doing on the shared channel.
    if (!this.started) {
      return;
    }

    dispatchApplyDataGridQueryResult({
      gridCode: this.deps.gridCode,
      dataQueryId: e.detail.dataQueryId,
      requestId: e.detail.requestId,
      outcome: settled === 'success' ? 'applied' : 'failed',
    });
  }

  private onSelectRowsEvent(e: CustomEvent<SelectEagridRowsCustomEvent>): void {
    if (e.detail.gridCode !== this.deps.gridCode) {
      return;
    }
    const state = this.deps.store.getState();
    match(state)
      .when(isSelectionDeferred, () => this.queuePendingSelectRows(e.detail.target, state))
      .otherwise(() => this.applyExternalSelection(e.detail.target));
  }

  private queuePendingSelectRows(target: SelectDatagridRowsTarget, state: EagGridState): void {
    if (this.pendingSelectRows.length === 0) {
      this.pendingSelectRowsBaselineRows = state.rows;
    }
    // A 'set' replaces the whole selection, so anything queued before it is dead work —
    // supersede instead of replaying (and overwriting) each superseded request on flush.
    this.pendingSelectRows =
      (target.mode ?? 'set') === 'set' ? [target] : [...this.pendingSelectRows, target];
  }

  private dropPendingSelectRows(): void {
    this.pendingSelectRows = [];
    this.pendingSelectRowsBaselineRows = null;
  }

  private applyExternalSelection(target: SelectDatagridRowsTarget): void {
    const state = this.deps.store.getState();
    let next: Set<string> | null = null;
    try {
      next = resolveExternalRowSelection({
        rows: state.rows,
        selectedRowIds: state.selectedRowIds,
        rowSelectMode: selectRowSelectMode(state),
        target,
      });
    } catch (error) {
      // A consumer predicate can throw (e.g. reading a column absent from the loaded rows —
      // rows only carry visible + predefined columns). The channel is fire-and-forget, so
      // drop the request instead of letting the throw escape the window listener.
      captureException(error);
      return;
    }
    // null = nothing to apply (selection disabled, SINGLE-mode violation, all-miss 'set',
    // or no membership change) — skip the store write so subscribers are not re-notified
    // with an equal Set.
    if (isNotNil(next)) {
      this.deps.store.setSelectedRowIds(next);
    }
  }

  /** Applies queued external selection requests once the grid is resolvable again.
   *  Called from onStoreChangeDispatch, so the settling store write itself triggers the
   *  flush. Gates on LIVE store state, never the dispatch's snapshot — an earlier reaction
   *  row in the same dispatch can synchronously start a fetch (engine.fetchPage →
   *  startFetch before its first await), and the snapshot would let the queue drain
   *  against the outgoing rows. The queue is DROPPED (fire-and-forget parity) when the
   *  awaited fetch settles without landing rows — 'error' keeps the OLD page's rows, and
   *  the engine's finally safety net can settle 'success' after an unexpected throw with
   *  rows equally untouched (detected via the armed-time rows reference; setRows always
   *  stores a fresh array). Applying in either case would target exactly the stale rows
   *  the deferral exists to avoid. Draining the queue before applying keeps the nested
   *  onStoreChangeDispatch (from setSelectedRowIds) re-entrant safe. */
  private flushPendingSelectRows(): void {
    if (this.pendingSelectRows.length === 0) {
      return;
    }
    const state = this.deps.store.getState();
    match(state)
      .when(isSelectionDeferred, () => undefined)
      .when(
        (s) => s.fetchState === 'error' || s.rows === this.pendingSelectRowsBaselineRows,
        () => this.dropPendingSelectRows()
      )
      .otherwise(() => {
        const pending = this.pendingSelectRows;
        this.dropPendingSelectRows();
        pending.forEach((target) => this.applyExternalSelection(target));
      });
  }

  private onRefetchEvent(e: Event): void {
    const customEvent = e as CustomEvent<RefetchEagridCustomEvent>;
    if (customEvent.detail.gridCode !== this.deps.gridCode) {
      return;
    }
    // Clear stale promise locks and the page cache, then refetch the CURRENT query —
    // an external mutation (e.g. document upload) needs a full refetch with a fresh
    // /count. refetchQuery defeats the pagination fast path WITHOUT discarding the
    // snapshot's preset baseline: a full snapshot reset made the next fetch look like
    // a preset change and routed it to the preset's ORIGINAL dataQueryId, reverting a
    // filter/sort-modified view to stale data (the "Refresh view" button bug).
    this.deps.store.clearPromiseLocks();
    clearPageDataCacheForGrid(this.deps.gridCode);
    this.engine.refetchQuery(offsetOf(this.deps.store.getState()));
    if (customEvent.detail.options?.deselectAll) {
      this.deps.store.deselectAll();
    }
  }
}
